import { useState } from "react";
import { cn } from "@/lib/utils";

type ProgressionPath = "white" | "yellow" | "orange";

const BELT_SELECTOR: { id: ProgressionPath; label: string; beltImg: string }[] = [
  { id: "white", label: "White", beltImg: "/images/belts/white_belt.png" },
  { id: "yellow", label: "Yellow", beltImg: "/images/belts/yellow_belt.png" },
  { id: "orange", label: "Orange", beltImg: "/images/belts/orange_belt.png" },
];

export function LilDragonsProgressionTabContent() {
  const [activePath, setActivePath] = useState<ProgressionPath>("white");
  return (
    <div>
      {/* Mobile only: image → text → belt selector → single pathway (smaller image). */}
      <div className="flex flex-col gap-6 md:hidden">
        <div className="flex justify-center">
          <img
            src="/images/dragons/dragon_thumb.png"
            alt="Lil' Dragon progression"
            className="max-w-[15rem] w-full h-auto"
            loading="lazy"
            decoding="async"
          />
        </div>
        <p className="text-center text-sm text-gray-700">
          This is a Lil&apos; Dragon&apos;s journey to becoming a big dragon.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          {BELT_SELECTOR.map(({ id, label, beltImg }) => {
            const isSelected = activePath === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActivePath(id)}
                className={cn(
                  "flex flex-col items-center gap-1 rounded-xl border-2 bg-white p-3 transition-all min-w-[4.5rem]",
                  isSelected
                    ? "border-taekwondo-blue shadow-md shadow-taekwondo-blue/20 ring-2 ring-taekwondo-blue/20"
                    : "border-gray-200 hover:border-taekwondo-blue/40",
                )}
                aria-pressed={isSelected}
                aria-label={`${label} belt pathway`}
              >
                <img src={beltImg} alt="" className="h-12 w-auto object-contain" />
                <span className="text-xs font-semibold text-gray-900">{label}</span>
              </button>
            );
          })}
        </div>
        <div className="bg-gray-50 rounded-lg p-4 md:p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-taekwondo-red mb-3">My Progression</h3>
          <div className="text-sm">
            {activePath === "white" && <WhitePathway />}
            {activePath === "yellow" && <YellowPathway />}
            {activePath === "orange" && <OrangePathway />}
          </div>
        </div>
        <p className="text-xs text-gray-500">
          Each colour belt shows a step on the journey as Lil&apos; Dragons earn new tags and grades.
        </p>
      </div>

      {/* Desktop: original page markup (straight transfer). */}
      <div className="mt-8 hidden md:flex md:flex-row md:items-center md:gap-8">
        <div className="order-2 md:order-1 md:w-2/3">
          <div className="bg-gray-50 rounded-lg p-4 md:p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-taekwondo-red mb-3">My Progression</h3>
            <p className="text-sm text-gray-700 mb-4">
              This is a Lil&apos; Dragon&apos;s journey to becoming a big dragon.
            </p>
            <div className="grid grid-cols-1 gap-6 text-sm md:grid-cols-3">
              <div className="md:border-r md:border-gray-200 md:pr-4">
                <WhitePathway />
              </div>
              <div className="md:border-r md:border-gray-200 md:pr-4 md:pl-4">
                <YellowPathway />
              </div>
              <div className="md:pl-4">
                <OrangePathway />
              </div>
            </div>
            <p className="mt-4 text-xs text-gray-500">
              Each colour belt shows a step on the journey as Lil&apos; Dragons earn new tags and grades.
            </p>
          </div>
        </div>
        <div className="order-1 mt-6 flex justify-center md:order-2 md:mt-0 md:w-1/3">
          <img
            src="/images/dragons/dragon_thumb.png"
            alt="Lil' Dragon progression"
            className="max-w-[15rem] w-full h-auto"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    </div>
  );
}

function WhitePathway() {
  return (
<div className="space-y-2">
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/white_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <span className="inline-block px-3 py-1 rounded-full bg-gray-900 text-white font-semibold text-xs">
      WHITE
    </span>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/white_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full bg-gray-900 text-white font-semibold text-xs">
        WHITE
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-yellow-400 font-semibold text-xs">
        YELLOW
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/white_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full bg-gray-900 text-white font-semibold text-xs">
        WHITE
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-orange-400 font-semibold text-xs">
        ORANGE
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/white_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full bg-gray-900 text-white font-semibold text-xs">
        WHITE
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-green-500 font-semibold text-xs">
        GREEN
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/white_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full bg-gray-900 text-white font-semibold text-xs">
        WHITE
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-blue-500 font-semibold text-xs">
        BLUE
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/white_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full bg-gray-900 text-white font-semibold text-xs">
        WHITE
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-purple-500 font-semibold text-xs">
        PURPLE
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/white_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full bg-gray-900 text-white font-semibold text-xs">
        WHITE
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-amber-800 font-semibold text-xs">
        BROWN
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/white_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full bg-gray-900 text-white font-semibold text-xs">
        WHITE
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-red-500 font-semibold text-xs">
        RED
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/white_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full bg-gray-900 text-white font-semibold text-xs">
        WHITE
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-black font-semibold text-xs">
        BLACK
      </span>
    </div>
  </div>
</div>

  );
}

function YellowPathway() {
  return (
<div className="space-y-2">
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/yellow_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <span className="inline-block px-3 py-1 rounded-full text-yellow-400 font-semibold text-xs">
      YELLOW
    </span>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/yellow_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full text-yellow-400 font-semibold text-xs">
        YELLOW
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full bg-gray-900 text-white font-semibold text-xs">
        WHITE
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/yellow_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full text-yellow-400 font-semibold text-xs">
        YELLOW
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-orange-400 font-semibold text-xs">
        ORANGE
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/yellow_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full text-yellow-400 font-semibold text-xs">
        YELLOW
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-green-500 font-semibold text-xs">
        GREEN
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/yellow_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full text-yellow-400 font-semibold text-xs">
        YELLOW
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-blue-500 font-semibold text-xs">
        BLUE
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/yellow_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full text-yellow-400 font-semibold text-xs">
        YELLOW
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-purple-500 font-semibold text-xs">
        PURPLE
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/yellow_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full text-yellow-400 font-semibold text-xs">
        YELLOW
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-amber-800 font-semibold text-xs">
        BROWN
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/yellow_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full text-yellow-400 font-semibold text-xs">
        YELLOW
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-red-500 font-semibold text-xs">
        RED
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/yellow_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full text-yellow-400 font-semibold text-xs">
        YELLOW
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-black font-semibold text-xs">
        BLACK
      </span>
    </div>
  </div>
</div>

  );
}

function OrangePathway() {
  return (
<div className="space-y-2">
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/orange_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <span className="inline-block px-3 py-1 rounded-full text-orange-400 font-semibold text-xs">
      ORANGE
    </span>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/orange_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full text-orange-400 font-semibold text-xs">
        ORANGE
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full bg-gray-900 text-white font-semibold text-xs">
        WHITE
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/orange_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full text-orange-400 font-semibold text-xs">
        ORANGE
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-yellow-400 font-semibold text-xs">
        YELLOW
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/orange_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full text-orange-400 font-semibold text-xs">
        ORANGE
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-green-500 font-semibold text-xs">
        GREEN
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/orange_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full text-orange-400 font-semibold text-xs">
        ORANGE
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-blue-500 font-semibold text-xs">
        BLUE
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/orange_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full text-orange-400 font-semibold text-xs">
        ORANGE
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-purple-500 font-semibold text-xs">
        PURPLE
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/orange_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full text-orange-400 font-semibold text-xs">
        ORANGE
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-amber-800 font-semibold text-xs">
        BROWN
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/orange_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full text-orange-400 font-semibold text-xs">
        ORANGE
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-red-500 font-semibold text-xs">
        RED
      </span>
    </div>
  </div>
  <div className="flex items-center gap-2">
    <img
      src="/images/belts/orange_belt.png"
      alt="White belt"
      className="h-8 w-auto flex-shrink-0"
    />
    <div>
      <span className="inline-block px-3 py-1 rounded-full text-orange-400 font-semibold text-xs">
        ORANGE
      </span>
      <span className="mx-1 text-gray-500">/</span>
      <span className="inline-block px-3 py-1 rounded-full text-black font-semibold text-xs">
        BLACK
      </span>
    </div>
  </div>
</div>

  );
}
